import { Container, Graphics } from '../../../vendor/pixi.min.mjs';
import { BaseEntity } from '../../entities/BaseEntity.js';
import { FROGGER_CONFIG } from './froggerConfig.js';

/* ── Driving-feel animation tuning (per-frame on each vehicle). ──
 * Keep amplitudes sub-pixel-ish: the goal is "alive", not "shaky".
 *   ANIM_RATE         — radians/frame for the master bounce/tilt phase
 *   BOUNCE_AMP_PX     — vertical hop amplitude
 *   TILT_AMP_RAD      — rocking rotation amplitude
 *   VIBRATION_AMP_PX  — per-frame random rattle
 *   SHADOW_*          — ground-shadow scale + alpha modulation
 *   STREAK_*          — speed-line streak parameters (always visible,
 *                       cycle rate and intensity scale with effSpeed)
 */
const VEHICLE_ANIM_RATE       = [0.22, 0.38];
const VEHICLE_BOUNCE_AMP_PX   = 0.7;
const VEHICLE_TILT_AMP_RAD    = 0.0045;
const VEHICLE_VIBRATION_AMP   = 0.22;
const VEHICLE_SHADOW_SCALE    = 0.05;
const VEHICLE_SHADOW_ALPHA    = [0.20, 0.28];
const VEHICLE_STREAK_COUNT    = 3;
const VEHICLE_STREAK_LEN_PX   = 14;
/** effSpeed at which streak intensity peaks (linear from 0 → this). */
const VEHICLE_STREAK_FULL_AT  = 3.5;
/** Per-frame phase increment at effSpeed = 0 (keeps streaks alive on slow lanes). */
const VEHICLE_STREAK_RATE_BASE = 0.010;
/** Additional per-frame phase increment per unit of speed01 (0..1). */
const VEHICLE_STREAK_RATE_GAIN = 0.024;
/** Peak streak alpha at the slow end (speed01 = 0). */
const VEHICLE_STREAK_MIN_ALPHA = 0.22;
/** Peak streak alpha at full speed (speed01 = 1). */
const VEHICLE_STREAK_MAX_ALPHA = 0.55;
/** Trail extension (px) at slow end. */
const VEHICLE_STREAK_TRAIL_BASE = 10;
/** Additional trail extension (px) added at full speed. */
const VEHICLE_STREAK_TRAIL_GAIN = 14;

/**
 * A vehicle that moves horizontally across a lane.
 * Designed to be reused via ObjectPool — call configure() to set up for each spawn.
 */
export class FroggerVehicle extends BaseEntity {
    /** @type {number} px per delta unit */
    speed = 0;

    /** @type {number} 1 = right, -1 = left */
    direction = 1;

    /** @type {string} Asset key */
    _vehicleType = 'car';

    _themeManager = null;

    /** @type {number} The world row this vehicle currently belongs to */
    worldRow = 0;

    _driftTarget = NaN;
    _driftStartY = NaN;
    _driftProgress = 0;
    _driftCooldown = 0;

    /** @type {Container|null} Wrap that bounces / tilts around the vehicle center. */
    _visualWrap = null;
    /** @type {Graphics|null} Ground shadow under the vehicle. */
    _shadowGfx = null;
    /** @type {Graphics|null} Speed-line streaks trailing behind the vehicle. */
    _streakGfx = null;
    _animPhase = Math.random() * Math.PI * 2;
    _animSpeed = 0;
    /** Independent phase (0..1) driving the speed-line cycle for THIS vehicle. */
    _streakPhase = Math.random();

    constructor(themeManager = null) {
        super(0, 0, 1, 1);
        this._themeManager = themeManager;
    }

    /**
     * Set up this vehicle for a new spawn.
     * @param {Object} params
     * @param {string} params.vehicleType
     * @param {number} params.x
     * @param {number} params.y
     * @param {number} params.w
     * @param {number} params.h
     * @param {number} params.speed
     * @param {number} params.direction
     */
    configure({ vehicleType, x, y, w, h, speed, direction, worldRow = 0 }) {
        this._vehicleType = vehicleType;
        this.hitbox.width = w;
        this.hitbox.height = h;
        this.speed = speed;
        this.direction = direction;
        this.x = x;
        this.y = y;
        this.active = true;
        this.worldRow = worldRow;
        this._driftTarget = NaN;
        this._driftStartY = NaN;
        this._driftProgress = 0;
        this._driftCooldown = 0;
        this._animPhase = Math.random() * Math.PI * 2;
        this._animSpeed = VEHICLE_ANIM_RATE[0]
            + Math.random() * (VEHICLE_ANIM_RATE[1] - VEHICLE_ANIM_RATE[0]);
        this._streakPhase = Math.random();

        this._rebuildVisual();
        this.syncPosition();
    }

    update(delta, liveSpeedFactor = 1.0) {
        this.x += this.speed * this.direction * delta * liveSpeedFactor;

        if (this._driftCooldown > 0) this._driftCooldown -= delta;

        if (!isNaN(this._driftTarget)) {
            this._driftProgress += delta / FROGGER_CONFIG.LANE_CHANGE_FRAMES;
            if (this._driftProgress >= 1) {
                this.y = this._driftTarget;
                this._driftTarget = NaN;
                this._driftProgress = 0;
                this._driftCooldown = FROGGER_CONFIG.LANE_CHANGE_COOLDOWN;
            } else {
                this.y = this._driftStartY + (this._driftTarget - this._driftStartY) * this._driftProgress;
            }
        }

        this._updateDriveAnimation(delta, liveSpeedFactor);
        this.syncPosition();
    }

    get isDrifting() { return !isNaN(this._driftTarget); }
    get canDrift() { return this._driftCooldown <= 0 && !this.isDrifting; }

    startDrift(targetWorldRow) {
        const targetY = -targetWorldRow * FROGGER_CONFIG.CELL_SIZE
            + (FROGGER_CONFIG.CELL_SIZE - this.hitbox.height) / 2;
        this._driftStartY = this.y;
        this._driftTarget = targetY;
        this._driftProgress = 0;
        this.worldRow = targetWorldRow;
    }

    /**
     * Check if vehicle is off-screen and should be recycled.
     * @param {number} screenWidth
     * @returns {boolean}
     */
    isOffScreen(screenWidth) {
        if (this.direction > 0) {
            return this.x > screenWidth + 50;
        }
        return this.x + this.hitbox.width < -50;
    }

    _rebuildVisual() {
        this.display.removeChildren();
        const w = this.hitbox.width;
        const h = this.hitbox.height;

        // Layer 1: ground shadow (animated separately).
        const shadow = new Graphics();
        shadow.ellipse(0, 0, w * 0.42, h * 0.08);
        shadow.fill({ color: 0x000000, alpha: VEHICLE_SHADOW_ALPHA[0] });
        shadow.x = w / 2;
        shadow.y = h * 0.97;
        this._shadowGfx = shadow;
        this.display.addChild(shadow);

        // Layer 2: speed-line streaks (redrawn each frame).
        const streaks = new Graphics();
        this._streakGfx = streaks;
        this.display.addChild(streaks);

        // Layer 3: visual wrap centered on the vehicle so we can bounce/tilt
        // around its middle without offsetting its grid position.
        const wrap = new Container();
        wrap.x = w / 2;
        wrap.y = h / 2;
        this._visualWrap = wrap;
        this.display.addChild(wrap);

        const slotKey = `frogger.vehicle.${this._vehicleType}`;
        const frontDirection = this._themeManager?.getSetting(slotKey, 'frontDirection', 'right');
        const shouldFaceRight = this.direction > 0;
        const assetFacesRight = frontDirection !== 'left';
        const flipX = shouldFaceRight !== assetFacesRight;

        const sprite = this._themeManager?.createSprite(
            slotKey, w, h,
            { anchorX: 0.5, anchorY: 0.5, flipX }
        );
        if (sprite) {
            sprite.x = 0;
            sprite.y = 0;
            wrap.addChild(sprite);
            return;
        }

        // Procedural fallback: graphics are drawn from (0,0) to (w,h);
        // wrap them in an offset container so the center is at wrap origin.
        const g = new Graphics();
        this._drawVehiclePlaceholder(g);
        if (shouldFaceRight) {
            g.scale.x = 1;
            g.x = 0;
        } else {
            g.scale.x = -1;
            g.x = w;
        }
        const inner = new Container();
        inner.addChild(g);
        inner.x = -w / 2;
        inner.y = -h / 2;
        wrap.addChild(inner);
        this._graphics = g;
    }

    /**
     * Per-frame "driving" feel — vertical micro-bounce, slight tilt, sub-pixel
     * vibration, animated shadow, and speed-line streaks that scale with the
     * vehicle's current effective speed.
     */
    _updateDriveAnimation(delta, liveSpeedFactor = 1.0) {
        if (!this._visualWrap) return;
        const w = this.hitbox.width;
        const h = this.hitbox.height;

        this._animPhase += this._animSpeed * delta;
        const bounce = Math.sin(this._animPhase);
        const tilt   = Math.cos(this._animPhase * 1.13);
        const upness = Math.abs(bounce);
        const vibX = (Math.random() - 0.5) * VEHICLE_VIBRATION_AMP;
        const vibY = (Math.random() - 0.5) * VEHICLE_VIBRATION_AMP;

        this._visualWrap.x = w / 2 + vibX;
        this._visualWrap.y = h / 2 - upness * VEHICLE_BOUNCE_AMP_PX + vibY;
        this._visualWrap.rotation = tilt * VEHICLE_TILT_AMP_RAD;

        if (this._shadowGfx) {
            const grow = 1 + upness * VEHICLE_SHADOW_SCALE;
            this._shadowGfx.scale.set(grow, grow);
            this._shadowGfx.alpha = VEHICLE_SHADOW_ALPHA[1]
                - upness * (VEHICLE_SHADOW_ALPHA[1] - VEHICLE_SHADOW_ALPHA[0]);
        }

        const effSpeed = Math.max(0, this.speed * liveSpeedFactor);
        const speed01 = Math.min(1, effSpeed / VEHICLE_STREAK_FULL_AT);
        // Per-vehicle phase advances at a rate proportional to its own speed,
        // so a slow lane visibly cycles slower than a fast lane.
        const rate = VEHICLE_STREAK_RATE_BASE + speed01 * VEHICLE_STREAK_RATE_GAIN;
        this._streakPhase = (this._streakPhase + rate * delta) % 1;
        if (this._streakPhase < 0) this._streakPhase += 1;

        if (this._streakGfx) {
            this._drawSpeedLines(w, h, this.direction, speed01);
        }
    }

    _drawSpeedLines(w, h, dir, speed01) {
        const g = this._streakGfx;
        g.clear();

        const alphaScale = VEHICLE_STREAK_MIN_ALPHA
            + (VEHICLE_STREAK_MAX_ALPHA - VEHICLE_STREAK_MIN_ALPHA) * speed01;
        const trailLen = VEHICLE_STREAK_TRAIL_BASE
            + VEHICLE_STREAK_TRAIL_GAIN * speed01;

        for (let i = 0; i < VEHICLE_STREAK_COUNT; i++) {
            const localPhase = (this._streakPhase + i / VEHICLE_STREAK_COUNT) % 1;
            const lineY = h * (0.30 + i * 0.20);
            const trail = localPhase * trailLen;
            const startX = dir > 0 ? -8 - trail : w + 8 + trail;
            const alpha = (1 - localPhase) * alphaScale;
            g.rect(startX, lineY - 1, VEHICLE_STREAK_LEN_PX, 2);
            g.fill({ color: 0xffffff, alpha });
        }
    }

    _drawVehiclePlaceholder(g) {
        switch (this._vehicleType) {
            case 'bike':
                this._drawBike(g);
                break;
            case 'truck':
                this._drawTruck(g);
                break;
            case 'bus':
                this._drawTram(g);
                break;
            case 'car':
            default:
                this._drawCar(g);
                break;
        }
    }

    _drawCar(g) {
        const w = this.hitbox.width;
        const h = this.hitbox.height;
        g.roundRect(0, h * 0.22, w, h * 0.56, 12);
        g.fill(0xef4444);
        g.roundRect(w * 0.18, h * 0.06, w * 0.46, h * 0.3, 10);
        g.fill(0xf87171);
        this._drawWindows(g, w, h, 2);
        this._drawWheels(g, w, h, 2);
    }

    _drawTruck(g) {
        const w = this.hitbox.width;
        const h = this.hitbox.height;
        g.roundRect(0, h * 0.26, w * 0.68, h * 0.5, 10);
        g.fill(0x3b82f6);
        g.roundRect(w * 0.62, h * 0.16, w * 0.38, h * 0.6, 10);
        g.fill(0x60a5fa);
        g.roundRect(w * 0.7, h * 0.26, w * 0.18, h * 0.18, 4);
        g.fill(0xbfe7ff);
        this._drawWheels(g, w, h, 3);
    }

    _drawTram(g) {
        const w = this.hitbox.width;
        const h = this.hitbox.height;
        g.roundRect(0, h * 0.14, w, h * 0.72, 14);
        g.fill(0xfacc15);
        g.roundRect(w * 0.76, h * 0.14, w * 0.24, h * 0.72, 14);
        g.fill(0xeab308);
        g.roundRect(w * 0.08, h * 0.47, w * 0.84, h * 0.08, 3);
        g.fill(0xa16207);
        this._drawWindows(g, w, h, 4);
        this._drawWheels(g, w, h, 3);
    }

    _drawBike(g) {
        const w = this.hitbox.width;
        const h = this.hitbox.height;
        const cy = h * 0.68;
        const r = h * 0.18;
        g.circle(w * 0.24, cy, r);
        g.fill(0x111827);
        g.circle(w * 0.76, cy, r);
        g.fill(0x111827);

        g.circle(w * 0.24, cy, r * 0.52);
        g.fill(0x9ca3af);
        g.circle(w * 0.76, cy, r * 0.52);
        g.fill(0x9ca3af);

        g.moveTo(w * 0.24, cy);
        g.lineTo(w * 0.44, h * 0.42);
        g.lineTo(w * 0.58, cy);
        g.lineTo(w * 0.38, cy);
        g.closePath();
        g.fill(0xf97316);
        g.stroke({ color: 0xf59e0b, width: 3 });

        g.moveTo(w * 0.44, h * 0.42);
        g.lineTo(w * 0.62, h * 0.32);
        g.moveTo(w * 0.58, cy);
        g.lineTo(w * 0.7, h * 0.34);
        g.moveTo(w * 0.18, h * 0.36);
        g.lineTo(w * 0.3, h * 0.36);
        g.stroke({ color: 0xf3f4f6, width: 3 });
    }

    _drawWindows(g, w, h, count) {
        const windowW = w * (count === 4 ? 0.12 : 0.16);
        const windowH = h * 0.16;
        const totalW = windowW * count + (count - 1) * w * 0.04;
        let x = w / 2 - totalW / 2;
        const y = h * 0.3;
        for (let i = 0; i < count; i++) {
            g.roundRect(x, y, windowW, windowH, 4);
            g.fill(0xbfe7ff);
            x += windowW + w * 0.04;
        }
    }

    _drawWheels(g, w, h, count) {
        const y = h * 0.82;
        const r = h * 0.11;
        if (count === 2) {
            g.circle(w * 0.22, y, r);
            g.fill(0x111827);
            g.circle(w * 0.78, y, r);
            g.fill(0x111827);
            return;
        }
        if (count === 3) {
            g.circle(w * 0.18, y, r);
            g.fill(0x111827);
            g.circle(w * 0.5, y, r);
            g.fill(0x111827);
            g.circle(w * 0.82, y, r);
            g.fill(0x111827);
        }
    }
}
